<script setup lang="ts">
import type { ReportTemplate } from "@/types/report-templates.types";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import { useDownloadFile } from "@/stores/queries/files.query";
import type { FileEntity } from "@/types/file.types";
import DRDropdownItem from "../UI/DRDropdownItem.vue";
import {
  useDeleteReportTemplate,
  useUpdateReportTemplate,
} from "@/stores/queries/report-templates.query";
import { computed, ref } from "vue";
import DREditTextDialog from "../UI/feedbacks/DREditTextDialog.vue";
import ConfirmationModal from "../generic/ConfirmationModal.vue";
import { useTranslation } from "i18next-vue";

const props = withDefaults(
  defineProps<{
    template: ReportTemplate;
    size?: number;
  }>(),
  {
    size: 30,
  }
);

const emits = defineEmits<{
  (e: "deleted"): void;
}>();

const { t } = useTranslation();

const templateId = computed(() => props.template.id);

const { mutateAsync: mutateDownload, isPending: isDownloading } =
  useDownloadFile();
const download = async (file: FileEntity) => {
  mutateDownload(file);
};

const { mutateAsync: updateReportTemplate, isPending: isUpdating } =
  useUpdateReportTemplate(templateId);
const { mutateAsync: deleteReportTemplate, isPending: isDeleting } =
  useDeleteReportTemplate(templateId);

const isLoading = computed(
  () => isUpdating.value || isDeleting.value || isDownloading.value
);

async function renameReportTemplate(name: string) {
  await updateReportTemplate({
    name,
  });
}

const editing = ref(false);

const rules = [
  {
    validator: (value: string) => !!value.trim()?.length,
    message: t("dashboard:templates.form.validation.name_required"),
  },
];

const confirmDelete = ref(false);

const handleConfirmDelete = async () => {
  await deleteReportTemplate(undefined, {
    onSuccess: () => {
      confirmDelete.value = false;
      emits("deleted");
    },
  });
};
</script>

<template>
  <el-dropdown trigger="click" @click.stop.prevent>
    <DRBtnIcon
      :disabled="isLoading"
      icon="bi-three-dots"
      variant="flat"
      :size="size"
      @click.stop.prevent
      @dblclick.stop.prevent
    />

    <template #dropdown>
      <el-dropdown-menu>
        <DRDropdownItem
          @select="editing = true"
          icon="bi-pencil"
          :label="$t('dashboard:templates.actions.rename')"
        />
        <DREditTextDialog
          v-model="editing"
          :title="$t('dashboard:templates.renameDialog.title')"
          :value="template.name"
          :placeholder="$t('dashboard:templates.renameDialog.placeholder')"
          :rules="rules"
          @confirm="renameReportTemplate($event)"
        />

        <DRDropdownItem
          v-if="template.templateFile"
          icon="bi-download"
          :label="$t('dashboard:templates.actions.download')"
          @select="download(template.templateFile)"
        />

        <DRDropdownItem
          @select="confirmDelete = true"
          icon="bi-trash3"
          :label="$t('dashboard:templates.actions.delete')"
          type="danger"
        />

        <ConfirmationModal
          v-model="confirmDelete"
          @confirm="handleConfirmDelete()"
          @cancel="confirmDelete = false"
          :label="$t('delete')"
          danger
          :message="
            $t('dashboard:templates.deleteDialog.message', {
              name: template.name,
            })
          "
        />
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<style lang="scss" scoped></style>
